import { ToastManagerProp, withToastManager } from 'react-toast-notifications';

import Dialog from 'components/Dialog';
import Tip from 'components/Tip';
import ToggleSwitch from 'components/ToggleSwitch';
import { Button, Loader, PercentInput, TextField } from 'components/UtilElements';
import * as datetime from 'core/datetime';
import { DATE_FORMAT } from 'globals/constants';

import PollScores from './PollScores';
import QuizScores from './QuizScores';
import { UseBulkExportClassActivitiesDialog, useBulkExportClassActivitiesDialog } from './vm';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BulkExportClassActivitiesDialogProps
  extends UseBulkExportClassActivitiesDialog,
    ToastManagerProp {}

const BulkExportClassActivitiesDialog = ({ ...vmProps }: BulkExportClassActivitiesDialogProps) => {
  const { isOpen, onClose, activityType } = vmProps;
  const {
    dialogTitle,
    tipText,
    sectionTitle,
    isFetchingActivities,
    bulkExportActivities,
    normalizeScores,
    handleToggleNormalizeScores,
    normalizeTo,
    handleChangeNormalizeTo,
    exportAsPercentage,
    handleToggleExportAsPercentage,
    latePenalty,
    handleChangeLatePenalty,
    selectedActivityIds,
    toggleSelectedActivityId,
    handleSelectAllActivities,
    handleSelectNoneActivities,
    handleDeleteExportedScores,
    handleReExportScores,
    normalizeToError,
    latePenaltyError,
    isExportingScores,
    isDeletingExportedScores,
  } = useBulkExportClassActivitiesDialog(vmProps);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={dialogTitle}
      classes={{
        footer: 'bulk-export-footer',
      }}
      footer={
        <>
          {bulkExportActivities ? (
            <p>
              Last exported on :{' '}
              {bulkExportActivities.lastExportedOn <= 0
                ? 'Never'
                : datetime.format(bulkExportActivities.lastExportedOn, DATE_FORMAT)}
            </p>
          ) : null}
          <div className='footer__buttons'>
            <Button
              variant='text'
              color='error'
              size='large'
              preventMinWidth
              preventHoverStyles
              disabled={
                !bulkExportActivities || bulkExportActivities.lastExportedOn <= 0 || isDeletingExportedScores
              }
              onClick={handleDeleteExportedScores}
            >
              DELETE EXPORTED SCORES
            </Button>
            <Button
              variant='outlined'
              color='info'
              size='large'
              preventMinWidth
              onClick={handleReExportScores}
              disabled={!bulkExportActivities || selectedActivityIds.length === 0 || isExportingScores}
            >
              {!bulkExportActivities || bulkExportActivities.lastExportedOn <= 0 ? 'EXPORT' : 'RE-EXPORT'}
            </Button>
          </div>
        </>
      }
    >
      {isFetchingActivities ? (
        <div className='bulk-export-body'>
          <Loader />
        </div>
      ) : bulkExportActivities != null ? (
        <div className='bulk-export-body'>
          {tipText ? <Tip text={tipText} color='info' /> : null}
          <div className='row'>
            <h3>{sectionTitle}</h3>
            <div className='filters'>
              <span>Select: </span>
              <Button
                variant={
                  selectedActivityIds.length === bulkExportActivities.activities.length
                    ? 'contained'
                    : 'outlined'
                }
                onClick={handleSelectAllActivities}
              >
                All
              </Button>
              <Button
                variant={selectedActivityIds.length === 0 ? 'contained' : 'outlined'}
                onClick={handleSelectNoneActivities}
              >
                None
              </Button>
            </div>
          </div>
          <div className='table-wrapper'>
            {activityType === 'quiz' ? (
              <QuizScores
                quizzes={bulkExportActivities?.activities}
                selectedQuizIds={selectedActivityIds}
                toggleSelectedQuizId={toggleSelectedActivityId}
              />
            ) : (
              <PollScores
                polls={bulkExportActivities?.activities}
                selectedPollIds={selectedActivityIds}
                toggleSelectedPollId={toggleSelectedActivityId}
              />
            )}
          </div>
          {activityType === 'quiz' ? (
            <div className='row'>
              <div>
                <p>Normalize scores</p>
                <p className='helper-text'>All exported quizzes will have the same maximum score</p>
              </div>
              <ToggleSwitch isOn={normalizeScores} onClick={handleToggleNormalizeScores} />
            </div>
          ) : null}
          {normalizeScores ? (
            <div className='row'>
              <div>
                <p>{activityType === 'quiz' ? 'Normalize to' : 'Participation points per poll'}</p>
                {normalizeToError ? <p className='helper-text error'>{normalizeToError}</p> : null}
              </div>
              <TextField
                type='number'
                min={1}
                selector='activity__score-input'
                value={normalizeTo}
                onchange={handleChangeNormalizeTo}
                hasError={!!normalizeToError}
              />
            </div>
          ) : null}
          <div className='row'>
            <p>Export as percentage</p>
            <ToggleSwitch isOn={exportAsPercentage} onClick={handleToggleExportAsPercentage} />
          </div>
          <div className='row'>
            <div>
              <p>Late penalty</p>
              {latePenaltyError ? <p className='helper-text error'>{latePenaltyError}</p> : null}
            </div>
            <PercentInput
              selector='activity__score-input'
              initValue={latePenalty}
              onchange={handleChangeLatePenalty}
              placeholder='Penalty'
              hasError={!!latePenaltyError}
            />
          </div>
        </div>
      ) : null}
    </Dialog>
  );
};

export default withToastManager(BulkExportClassActivitiesDialog);
